/**
 * Contenuto dei messaggi speciali dentro la bolla: posizione, contatto (vCard), sondaggio.
 * Numeri Ufficiale: coordinate e contatti arrivano in Message.metadata (location / contacts).
 * Numeri Standard: i dati (coordinate, vCard, opzioni) stanno nel raw_payload della riga
 * whatsapp_messages: si leggono solo per questi tipi, una volta (insieme ai metadata, per i
 * messaggi arrivati senza).
 * Parsing in messageExtras.ts (puro, testato).
 */

import { useEffect, useState } from 'react';
import { MapPin, ExternalLink, BarChart2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { ContactCardRow } from './ContactCardRow';
import {
  resolveLocation, locationFromMetadata, mapsUrl, resolveContacts, contactsFromMetadata, extractPoll, tallyPoll, pollVotesLikePattern,
} from './messageExtras';

type RowExtras = { raw: unknown; metadata: unknown };
const rawCache = new Map<string, Promise<RowExtras>>();

/**
 * raw_payload e metadata della riga (in cache per id). Messaggi solo in memoria: null.
 * `skip`: i dati sono gia' nei metadata del messaggio, nessuna lettura dal DB.
 */
function useRawPayload(messageId: string, skip = false): { raw: unknown; metadata: unknown; loaded: boolean } {
  const [state, setState] = useState<{ raw: unknown; metadata: unknown; loaded: boolean }>({ raw: null, metadata: null, loaded: skip });
  useEffect(() => {
    if (skip) return;
    let cancelled = false;
    if (!rawCache.has(messageId)) {
      rawCache.set(messageId, Promise.resolve(
        supabase.from('whatsapp_messages').select('raw_payload, metadata').eq('id', messageId).maybeSingle()
      ).then(({ data }) => ({ raw: data?.raw_payload ?? null, metadata: data?.metadata ?? null }), () => ({ raw: null, metadata: null })));
    }
    rawCache.get(messageId)!.then((r) => { if (!cancelled) setState({ ...r, loaded: true }); });
    return () => { cancelled = true; };
  }, [messageId, skip]);
  return state;
}

const mutedText = (isOutbound: boolean) => (isOutbound ? 'text-[#667781] dark:text-[#8696A0]' : 'text-[#787774]');

// ============ POSIZIONE ============

export function LocationContent({ messageId, content, metadata, isOutbound }: { messageId: string; content?: string; metadata?: Record<string, unknown>; isOutbound: boolean }) {
  const fromMessage = locationFromMetadata(metadata);
  const fetched = useRawPayload(messageId, !!fromMessage);
  const loaded = fetched.loaded;
  const loc = fromMessage ?? resolveLocation(fetched.metadata, fetched.raw, content);
  const url = loc ? mapsUrl(loc) : null;
  if (!loaded && !loc) return <p className={`text-sm italic ${mutedText(isOutbound)}`}>Posizione...</p>;
  return (
    <div className="flex items-start gap-2 py-1 min-w-[200px]">
      <div className="w-9 h-9 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
        <MapPin className="w-5 h-5 text-green-600" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{loc?.name || 'Posizione condivisa'}</p>
        {loc?.address && <p className={`text-xs truncate ${mutedText(isOutbound)}`}>{loc.address}</p>}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-xs text-[#2383E2] hover:underline mt-0.5">
            Apri in Google Maps <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <p className={`text-xs ${mutedText(isOutbound)}`}>Coordinate non disponibili</p>
        )}
      </div>
    </div>
  );
}

// ============ CONTATTO ============

export function ContactContent({ messageId, content, metadata, isOutbound }: { messageId: string; content?: string; metadata?: Record<string, unknown>; isOutbound: boolean }) {
  const fromMessage = contactsFromMetadata(metadata);
  const fetched = useRawPayload(messageId, fromMessage.length > 0);
  const loaded = fetched.loaded;
  const cards = fromMessage.length > 0 ? fromMessage : resolveContacts(fetched.metadata, fetched.raw, content);
  if (!loaded && cards.length === 0) return <p className={`text-sm italic ${mutedText(isOutbound)}`}>Contatto...</p>;
  if (cards.length === 0) return <p className={`text-sm italic ${mutedText(isOutbound)}`}>Contatto condiviso (dettagli non disponibili)</p>;
  return (
    <div className="divide-y divide-[rgba(55,53,47,0.06)] dark:divide-white/10">
      {cards.map((c, i) => <ContactCardRow key={`${c.phone}-${i}`} card={c} mutedClass={mutedText(isOutbound)} />)}
    </div>
  );
}

// ============ SONDAGGIO ============

export function PollContent({ messageId, content, isOutbound }: { messageId: string; content?: string; isOutbound: boolean }) {
  const { raw } = useRawPayload(messageId);
  const [votes, setVotes] = useState<Array<{ selected_options: string[] | null }>>([]);

  useEffect(() => {
    let cancelled = false;
    supabase.from('whatsapp_poll_votes').select('selected_options')
      // Solo i voti di questo sondaggio in questa chat (non l'ultimo pezzo dell'id: nei gruppi e' chi scrive).
      .like('poll_message_id', pollVotesLikePattern(messageId))
      .then(({ data }) => { if (!cancelled && data) setVotes(data); });
    return () => { cancelled = true; };
  }, [messageId]);

  const poll = extractPoll(raw, content);
  const tally = tallyPoll(poll.options, votes);
  const total = votes.length;
  const max = Math.max(1, ...tally.map((t) => t.votes));

  return (
    <div className="py-1 min-w-[220px]">
      <div className="flex items-center gap-1.5 mb-1.5">
        <BarChart2 className="w-4 h-4 text-pink-500 flex-shrink-0" />
        <p className="text-sm font-medium">{poll.question}</p>
      </div>
      <div className="space-y-1.5">
        {tally.map((t) => (
          <div key={t.option}>
            <div className="flex justify-between text-xs"><span className="truncate">{t.option}</span><span className={mutedText(isOutbound)}>{t.votes}</span></div>
            <div className="h-1.5 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/10 overflow-hidden">
              <div className="h-full bg-[#25D366]" style={{ width: `${(t.votes / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
      <p className={`text-[11px] mt-1.5 ${mutedText(isOutbound)}`}>
        {total === 0 ? 'Ancora nessun voto' : total === 1 ? '1 persona ha votato' : `${total} persone hanno votato`}
      </p>
    </div>
  );
}
